/**
 * Safe Storage Utility
 * Provides fallback to in-memory storage when localStorage/sessionStorage is unavailable
 * (e.g., in Instagram/Facebook in-app browsers, private browsing, etc.)
 */

class SafeStorage {
  private memoryStorage: Map<string, string>;
  private storageType: 'localStorage' | 'sessionStorage';
  private isAvailable: boolean;
  private storage: Storage | null;

  constructor(storageType: 'localStorage' | 'sessionStorage') {
    this.storageType = storageType;
    this.memoryStorage = new Map();
    this.isAvailable = false;
    this.storage = null;

    this.initializeStorage();
  }

  private initializeStorage() {
    try {
      // Check if we're in a browser environment
      if (typeof window === 'undefined') {
        this.isAvailable = false;
        return;
      }

      // Check if the storage type is available
      if (this.storageType === 'localStorage') {
        this.storage = window.localStorage;
      } else {
        this.storage = window.sessionStorage;
      }

      // Test if storage is actually available and writable
      if (this.storage) {
        const testKey = '__safe_storage_test__';
        this.storage.setItem(testKey, 'test');
        this.storage.removeItem(testKey);
        this.isAvailable = true;
      }
    } catch (error) {
      // Storage is not available (e.g., in private browsing, in-app browsers)
      this.isAvailable = false;
      this.storage = null;
      console.warn(`SafeStorage: ${this.storageType} is not available, using in-memory fallback`);
    }
  }

  getItem(key: string): string | null {
    try {
      if (this.isAvailable && this.storage) {
        return this.storage.getItem(key);
      }
      // Fallback to memory storage
      return this.memoryStorage.get(key) || null;
    } catch (error) {
      console.warn(`SafeStorage: Error getting item "${key}":`, error);
      return this.memoryStorage.get(key) || null;
    }
  }

  setItem(key: string, value: string): void {
    try {
      if (this.isAvailable && this.storage) {
        this.storage.setItem(key, value);
        return;
      }
      // Fallback to memory storage
      this.memoryStorage.set(key, value);
    } catch (error) {
      console.warn(`SafeStorage: Error setting item "${key}":`, error);
      // Fallback to memory storage
      this.memoryStorage.set(key, value);
    }
  }

  removeItem(key: string): void {
    try {
      if (this.isAvailable && this.storage) {
        this.storage.removeItem(key);
      }
      // Also remove from memory storage
      this.memoryStorage.delete(key);
    } catch (error) {
      console.warn(`SafeStorage: Error removing item "${key}":`, error);
      // Fallback to memory storage
      this.memoryStorage.delete(key);
    }
  }

  clear(): void {
    try {
      if (this.isAvailable && this.storage) {
        this.storage.clear();
      }
      // Also clear memory storage
      this.memoryStorage.clear();
    } catch (error) {
      console.warn(`SafeStorage: Error clearing storage:`, error);
      // Fallback to memory storage
      this.memoryStorage.clear();
    }
  }

  key(index: number): string | null {
    try {
      if (this.isAvailable && this.storage) {
        return this.storage.key(index);
      }
      // Fallback to memory storage
      const keys = Array.from(this.memoryStorage.keys());
      return keys[index] || null;
    } catch (error) {
      console.warn(`SafeStorage: Error getting key at index ${index}:`, error);
      const keys = Array.from(this.memoryStorage.keys());
      return keys[index] || null;
    }
  }

  get length(): number {
    try {
      if (this.isAvailable && this.storage) {
        return this.storage.length;
      }
      // Fallback to memory storage
      return this.memoryStorage.size;
    } catch (error) {
      console.warn(`SafeStorage: Error getting length:`, error);
      return this.memoryStorage.size;
    }
  }

  // Check if storage is available
  isStorageAvailable(): boolean {
    return this.isAvailable;
  }

  // Get storage type
  getStorageType(): string {
    return this.isAvailable ? this.storageType : 'memory';
  }
}

// Create singleton instances
export const safeLocalStorage = new SafeStorage('localStorage');
export const safeSessionStorage = new SafeStorage('sessionStorage');

// Export the class for custom instances if needed
export { SafeStorage };

// Utility function to check if any storage is available
export const isStorageAvailable = (): boolean => {
  return safeLocalStorage.isStorageAvailable() || safeSessionStorage.isStorageAvailable();
};

// Utility function to get storage info for debugging
export const getStorageInfo = () => ({
  localStorage: {
    available: safeLocalStorage.isStorageAvailable(),
    type: safeLocalStorage.getStorageType(),
    length: safeLocalStorage.length
  },
  sessionStorage: {
    available: safeSessionStorage.isStorageAvailable(),
    type: safeSessionStorage.getStorageType(),
    length: safeSessionStorage.length
  }
});
